/**
 * React Query hooks for service-desk data.
 *
 * Query keys are centralised in `qk` so an invalidation can never miss a cache through a typo —
 * that class of bug is silent (stale board, no error) and therefore expensive to find.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import * as sd from '@/services/servicedesk';
import type {
  BulkUpdateRequest,
  Company,
  Contact,
  Page,
  Taxonomy,
  TicketCreate,
  TicketDetail,
  TicketFilters,
  TicketSummary,
  TicketUpdate,
} from '@/types/servicedesk';

export const qk = {
  tickets: (filters: TicketFilters) => ['tickets', filters] as const,
  ticket: (id: string) => ['ticket', id] as const,
  children: (id: string) => ['ticket', id, 'children'] as const,
  notes: (id: string) => ['ticket', id, 'notes'] as const,
  taxonomy: () => ['taxonomy'] as const,
  tags: (search?: string) => ['tags', search ?? ''] as const,
  companies: (params: object) => ['companies', params] as const,
  company: (id: string) => ['company', id] as const,
  companyLocations: (id: string) => ['company', id, 'locations'] as const,
  contacts: (params: object) => ['contacts', params] as const,
  contact: (id: string) => ['contact', id] as const,
  customFields: (entity: string) => ['custom-fields', entity] as const,
};

/**
 * Queues, statuses, priorities and types in one hook.
 *
 * These change roughly never but are needed by every board, filter bar and form, so they're
 * fetched together and held for the session rather than refetched per screen.
 */
export function useTaxonomy(): UseQueryResult<Taxonomy> {
  return useQuery({
    queryKey: qk.taxonomy(),
    queryFn: async (): Promise<Taxonomy> => {
      const [queues, statuses, priorities, types] = await Promise.all([
        sd.listQueues(),
        sd.listStatuses(),
        sd.listPriorities(),
        sd.listTypes(),
      ]);
      return { queues, statuses, priorities, types };
    },
    staleTime: 10 * 60_000,
  });
}

export function useTickets(filters: TicketFilters): UseQueryResult<Page<TicketSummary>> {
  return useQuery({
    queryKey: qk.tickets(filters),
    queryFn: () => sd.listTickets(filters),
    // Keeps the previous page on screen while the next one loads, so paging and filtering don't
    // flash an empty table.
    placeholderData: (previous) => previous,
    staleTime: 15_000,
  });
}

export function useTicket(id: string | undefined): UseQueryResult<TicketDetail> {
  return useQuery({
    queryKey: qk.ticket(id ?? ''),
    queryFn: () => sd.getTicket(id as string),
    enabled: Boolean(id),
  });
}

export function useTicketChildren(id: string | undefined) {
  return useQuery({
    queryKey: qk.children(id ?? ''),
    queryFn: () => sd.listChildren(id as string),
    enabled: Boolean(id),
  });
}

export function useTicketNotes(id: string | undefined) {
  return useQuery({
    queryKey: qk.notes(id ?? ''),
    queryFn: () => sd.listNotes(id as string),
    enabled: Boolean(id),
  });
}

export function useTags(search?: string) {
  return useQuery({ queryKey: qk.tags(search), queryFn: () => sd.listTags(search) });
}

export function useCustomFields(entityType: string) {
  return useQuery({
    queryKey: qk.customFields(entityType),
    queryFn: () => sd.listCustomFields(entityType),
    staleTime: 5 * 60_000,
  });
}

/**
 * Everything a ticket write must invalidate.
 *
 * A status change can move the ticket between board columns, change a parent's progress roll-up,
 * auto-close ancestors and add a system note — so the blunt invalidation is correct here. Being
 * clever about which caches survive is how a board ends up showing a ticket in two columns.
 */
function invalidateTicketViews(client: ReturnType<typeof useQueryClient>, id?: string): void {
  void client.invalidateQueries({ queryKey: ['tickets'] });
  if (id) {
    void client.invalidateQueries({ queryKey: ['ticket', id] });
  } else {
    void client.invalidateQueries({ queryKey: ['ticket'] });
  }
  // Ancestors' progress bars and children lists live under other ids.
  void client.invalidateQueries({ queryKey: ['ticket'], exact: false });
}

export function useCreateTicket() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: TicketCreate) => sd.createTicket(body),
    onSuccess: () => invalidateTicketViews(client),
  });
}

export function useUpdateTicket() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TicketUpdate }) => sd.updateTicket(id, body),
    onSuccess: (_data, variables) => invalidateTicketViews(client, variables.id),
  });
}

export function useAddNote(ticketId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { body: string; is_internal: boolean }) => sd.addNote(ticketId, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.notes(ticketId) });
      void client.invalidateQueries({ queryKey: qk.ticket(ticketId) });
    },
  });
}

export function useBulkUpdate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkUpdateRequest) => sd.bulkUpdate(body),
    onSuccess: () => invalidateTicketViews(client),
  });
}

export function useCompanies(params: {
  page?: number;
  page_size?: number;
  search?: string;
}): UseQueryResult<Page<Company>> {
  return useQuery({
    queryKey: qk.companies(params),
    queryFn: () => sd.listCompanies(params),
    placeholderData: (previous) => previous,
  });
}

export function useCompany(id: string | undefined): UseQueryResult<Company> {
  return useQuery({
    queryKey: qk.company(id ?? ''),
    queryFn: () => sd.getCompany(id as string),
    enabled: Boolean(id),
  });
}

export function useCompanyLocations(id: string | undefined) {
  return useQuery({
    queryKey: qk.companyLocations(id ?? ''),
    queryFn: () => sd.listCompanyLocations(id as string),
    enabled: Boolean(id),
  });
}

export function useContacts(params: {
  page?: number;
  page_size?: number;
  search?: string;
  company_id?: string;
}): UseQueryResult<Page<Contact>> {
  return useQuery({
    queryKey: qk.contacts(params),
    queryFn: () => sd.listContacts(params),
    placeholderData: (previous) => previous,
  });
}

export function useCreateCompany() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Company>) => sd.createCompany(body),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['companies'] }),
  });
}

export function useUpdateCompany() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Company> }) =>
      sd.updateCompany(id, body),
    onSuccess: (_d, v) => {
      void client.invalidateQueries({ queryKey: ['companies'] });
      void client.invalidateQueries({ queryKey: qk.company(v.id) });
    },
  });
}

export function useCreateContact() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Contact>) => sd.createContact(body),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['contacts'] }),
  });
}

export function useUpdateContact() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Contact> }) =>
      sd.updateContact(id, body),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['contacts'] }),
  });
}
